/**
 * Rasterise an html-ad-editor document to a PNG `Blob`, mirroring
 * `layers/utils/capture-svg-preview.ts` for the new HTML-based ads.
 *
 * Technique: HTML can't be loaded into an `<img>` directly, so we wrap it in
 * a single `<svg><foreignObject>` and use the same SVG-as-image-as-canvas
 * trick the SVG capture path uses. Remote image references are inlined to
 * `data:` URLs first so the rendered SVG is self-contained.
 */

import { resolveAdRefs } from "@hightouch/lib/ad-studio/html-editor/resolve-ad-refs";
import { clampOutputScale } from "src/pages/ad-studio/layers/utils/clamp-output-scale";
import {
  preloadFontsIntoDocument,
  removePreloadedFonts,
} from "src/pages/ad-studio/layers/utils/preload-fonts-into-document";
import {
  canvasToPngBlob,
  rasterizeImageToCanvas,
} from "src/pages/ad-studio/layers/utils/rasterize-image-to-blob";
import { UnreachableImageAssetError } from "src/utils/unreachable-image-asset-error";

const SVG_NS = "http://www.w3.org/2000/svg";
const XHTML_NS = "http://www.w3.org/1999/xhtml";
const DEFAULT_WIDTH = 800;
const DEFAULT_HEIGHT = 600;
const IMAGE_URL_ATTRIBUTES = [
  { selector: "img", attr: "src" },
  { selector: "image", attr: "href" },
  { selector: "image", attr: "xlink:href" },
] as const;
const CSS_URL_FUNCTION_RE =
  /url\(\s*(?:"([^"]+)"|'([^']+)'|([^)'"\\\s]+))\s*\)/g;

interface CaptureHtmlArgs {
  htmlString: string;
  fontRules?: string[];
  apiBase?: string;
  /** Filename → S3 storage key. Used to resolve `ad:` image refs to API URLs
   * before inlining. The static-ad agent writes HTML with `ad:` refs directly,
   * so without this map those images would render blank. */
  componentAssets?: Record<string, string> | null;
  /** Multiplier applied to the output pixel dimensions (e.g. 2 for retina
   * exports). Defaults to 1 — exact design-canvas dims — which ad exports
   * rely on to meet fixed-pixel platform specs. Mirrors `captureSvgAsImage`. */
  outputScale?: number;
  /** Cap on output width in px; clamps `outputScale` (never below 1). */
  maxOutputWidth?: number;
  /** Cap on total output pixels; clamps `outputScale` (never below 1). */
  maxOutputPixels?: number;
}

export async function captureHtmlAsImage(args: CaptureHtmlArgs): Promise<Blob> {
  return canvasToPngBlob(await captureHtmlToCanvas(args));
}

/** Same pipeline as `captureHtmlAsImage`, but raw RGBA — the PSD export needs
 * pixels rather than an encoded PNG. */
export async function captureHtmlAsImageData(
  args: Pick<
    CaptureHtmlArgs,
    "htmlString" | "fontRules" | "apiBase" | "componentAssets"
  >,
): Promise<ImageData> {
  const canvas = await captureHtmlToCanvas({ ...args, outputScale: 1 });
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("captureHtmlAsImageData: 2D canvas context unavailable");
  }
  return ctx.getImageData(0, 0, canvas.width, canvas.height);
}

async function captureHtmlToCanvas(
  args: CaptureHtmlArgs,
): Promise<HTMLCanvasElement> {
  const { htmlString, fontRules = [], apiBase, componentAssets } = args;
  if (fontRules.some((rule) => /^\s*@import\b/i.test(rule))) {
    throw new Error(
      "Adobe Fonts aren't supported in this image export. Export the static ad from Ad Studio instead.",
    );
  }

  const root = await prepareHtmlForCapture({
    htmlString,
    componentAssets,
    apiBase,
  });

  const width = readPx(root, "width") || DEFAULT_WIDTH;
  const height = readPx(root, "height") || DEFAULT_HEIGHT;
  const outputScale = clampOutputScale({
    scale: args.outputScale ?? 1,
    width,
    height,
    maxOutputWidth: args.maxOutputWidth,
    maxOutputPixels: args.maxOutputPixels,
  });
  const outputWidth = Math.round(width * outputScale);
  const outputHeight = Math.round(height * outputScale);

  // foreignObject treats its child as XHTML when an explicit xmlns is present.
  root.setAttribute("xmlns", XHTML_NS);
  const rootXml = new XMLSerializer().serializeToString(root);

  // `foreignObject` content gets browser UA defaults (content-box). The live
  // editor preview happens to work because the host page provides a global
  // border-box reset via Chakra; without it, any element written with explicit
  // `width` + `padding` (e.g. the highlight pill) inflates by the padding
  // amount on export. Inject the same reset so the captured PNG matches the
  // editor preview.
  const resetCss = "*, *::before, *::after { box-sizing: border-box; }";
  const defsCss = [resetCss, ...fontRules].join("\n");
  const svgString =
    `<svg xmlns="${SVG_NS}" width="${width}" height="${height}">` +
    `<defs><style>${escapeXml(defsCss)}</style></defs>` +
    `<foreignObject width="100%" height="100%">${rootXml}</foreignObject>` +
    `</svg>`;
  const svgDataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgString)}`;

  // An `<img>`-loaded SVG is an isolated document with no access to the parent
  // `document.fonts`, and `img.onload` for an SVG resolves on markup parse, not
  // on font decode. Preload the same embedded `@font-face` bytes into the
  // capturing document and await their decode BEFORE `drawImage`, so the brand
  // font is rasterized instead of a system fallback. Best-effort — a failed
  // font never aborts the export.
  const preloadedFonts = await preloadFontsIntoDocument(fontRules);

  try {
    const imgEl = new Image();
    imgEl.width = outputWidth;
    imgEl.height = outputHeight;
    const imgLoaded = new Promise<void>((resolve, reject) => {
      imgEl.onload = () => resolve();
      imgEl.onerror = () =>
        reject(new Error("Failed to load HTML as SVG image"));
      imgEl.src = svgDataUrl;
    });
    // Prefer `decode()` (resolves once the image is fully decoded and
    // paintable), falling back to the onload promise where `decode()` is
    // unavailable.
    await (typeof imgEl.decode === "function"
      ? imgEl.decode().catch(() => imgLoaded)
      : imgLoaded);

    return rasterizeImageToCanvas({
      image: imgEl,
      width: outputWidth,
      height: outputHeight,
    });
  } finally {
    // The preloaded faces were only needed for the isolated SVG's decode above;
    // drop them so repeated captures don't leak into `document.fonts`.
    removePreloadedFonts(preloadedFonts);
  }
}

export async function prepareHtmlForCapture(
  args: Pick<CaptureHtmlArgs, "htmlString" | "componentAssets" | "apiBase">,
): Promise<HTMLElement> {
  const root = parseRoot(args.htmlString);
  resolveAdRefs(root, args.componentAssets, args.apiBase);
  await inlineImages(root, args.apiBase);
  forceSynchronousImageDecoding(root);
  return root;
}

function parseRoot(htmlString: string): HTMLElement {
  const doc = new DOMParser().parseFromString(htmlString, "text/html");
  const root = doc.body.firstElementChild;
  if (!(root instanceof HTMLElement)) {
    throw new Error("captureHtmlAsImage: HTML has no root element");
  }
  return root;
}

async function inlineImages(
  root: HTMLElement,
  apiBase: string | undefined,
): Promise<void> {
  const imageRefs = IMAGE_URL_ATTRIBUTES.flatMap(({ selector, attr }) =>
    getMatchingElements(root, selector).map((el) => ({ el, attr })),
  );
  await Promise.all(
    imageRefs.map(async ({ el, attr }) => {
      const dataUrl = await fetchImageAsDataUrl(el.getAttribute(attr), apiBase);
      if (dataUrl) {
        el.setAttribute(attr, dataUrl);
      }
    }),
  );

  await Promise.all(
    getElementsWithStyle(root).map(async (el) => {
      const style = el.getAttribute("style");
      if (!style) return;
      el.setAttribute("style", await inlineCssImageUrls(style, apiBase));
    }),
  );

  await Promise.all(
    Array.from(root.querySelectorAll("style")).map(async (styleEl) => {
      const css = styleEl.textContent;
      if (!css) return;
      styleEl.textContent = await inlineCssImageUrls(css, apiBase);
    }),
  );
}

/**
 * WebKit decodes any image over 128,000 intrinsic pixels asynchronously
 * (`BitmapImageSource::isLargeForDecoding` — `w * h * 4 > 500 * KB`), and an
 * `<img>`-loaded SVG rasterises once with its image observer detached, so the
 * async decode never lands and the image is simply absent from the capture.
 * `decoding="sync"` is checked before that size heuristic and overrides it.
 *
 * Applies to every `<img>`, not just ones we inlined: `fetchImageAsDataUrl`
 * returns null for already-`data:` refs, which is what the static-ad agent
 * writes. SVG `<image>` renders via RenderSVGImage and is unaffected.
 */
export function forceSynchronousImageDecoding(root: HTMLElement): void {
  const images = [
    ...(root instanceof HTMLImageElement ? [root] : []),
    ...Array.from(root.querySelectorAll("img")),
  ];
  for (const img of images) {
    img.setAttribute("decoding", "sync");
  }
}

function getElementsWithStyle(root: HTMLElement): Element[] {
  const elements = Array.from(root.querySelectorAll("[style]"));
  if (root.hasAttribute("style")) {
    return [root, ...elements];
  }
  return elements;
}

function getMatchingElements(root: HTMLElement, selector: string): Element[] {
  const elements = Array.from(root.querySelectorAll(selector));
  if (root.matches(selector)) {
    return [root, ...elements];
  }
  return elements;
}

async function inlineCssImageUrls(
  value: string,
  apiBase: string | undefined,
): Promise<string> {
  const matches = Array.from(value.matchAll(CSS_URL_FUNCTION_RE));
  if (matches.length === 0) return value;

  let nextIndex = 0;
  let result = "";
  for (const match of matches) {
    const matchIndex = match.index;
    if (matchIndex === undefined) continue;

    result += value.slice(nextIndex, matchIndex);
    if (isCssFontOrImportUrl(value, matchIndex)) {
      result += match[0];
    } else {
      const url = match[1] ?? match[2] ?? match[3] ?? null;
      const dataUrl = await fetchImageAsDataUrl(url, apiBase);
      result += dataUrl ? `url("${dataUrl}")` : match[0];
    }
    nextIndex = matchIndex + match[0].length;
  }

  return result + value.slice(nextIndex);
}

// `@font-face` / `@import` `url()` tokens are fonts (or font stylesheets), not
// images. LCM-decomposed islands copy those remote URLs into nested `<style>`
// tags; font load failures are best-effort elsewhere (`preloadFontsIntoDocument`)
// and must not abort capture.
function isCssFontOrImportUrl(css: string, urlIndex: number): boolean {
  const prefix = css.slice(0, urlIndex);
  if (/@import\b[^;]*$/i.test(prefix)) {
    return true;
  }
  const lastFontFace = prefix.toLowerCase().lastIndexOf("@font-face");
  if (lastFontFace === -1) {
    return false;
  }
  return !prefix.slice(lastFontFace).includes("}");
}

async function fetchImageAsDataUrl(
  url: string | null,
  apiBase: string | undefined,
): Promise<string | null> {
  if (!url || url.startsWith("data:") || url.startsWith("#")) return null;
  const fetchUrl =
    url.includes("/agents/files?") && !url.includes("stream=")
      ? `${url}&stream=true`
      : url;
  const includeCredentials = !!apiBase && fetchUrl.includes(apiBase);
  try {
    const res = await fetch(
      fetchUrl,
      includeCredentials
        ? { credentials: "include" }
        : { mode: "cors", cache: "no-store" },
    );
    if (!res.ok) {
      throw new UnreachableImageAssetError(url);
    }
    const blob = await res.blob();
    return await blobToDataUrl(blob);
  } catch (error) {
    if (error instanceof UnreachableImageAssetError) {
      throw error;
    }
    throw new UnreachableImageAssetError(url);
  }
}

function readPx(el: HTMLElement, prop: "width" | "height"): number {
  const raw = el.style.getPropertyValue(prop);
  const n = Number.parseFloat(raw);
  return Number.isFinite(n) ? n : 0;
}

function escapeXml(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;");
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}
